import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import IdsDraggable from '../ids-draggable/ids-draggable';

import styles from './ids-splitter-bar.scss';

/**
 * IDS Splitter Bar Component
 * @type {IdsSplitterBar}
 * @inherits IdsDraggable
 */
@customElement('ids-splitter-bar')
@scss(styles)
export default class IdsSplitterBar extends IdsDraggable {
  constructor() {
    super();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array<string>} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.LABEL,
      attributes.SHOW_EXPAND_BUTTON,
    ];
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.#renderBarContent();
  }

  disconnectedCallback(): void {
    this.#detachExpandButtonEvents();
    super.disconnectedCallback?.();
  }

  /**
   * Render the split bar visual content as light DOM children.
   * Light DOM is used so the parent splitter's shadow DOM CSS can style these elements.
   * @private
   */
  #renderBarContent(): void {
    if (this.querySelector('.split-bar')) return;
    const html = `<div class="split-bar" part="split-bar" role="presentation">
      <div class="splitter-drag-handle"></div>
    </div>`;
    this.insertAdjacentHTML('beforeend', html);
    if (this.showExpandButton) {
      this.#addExpandButton();
    }
  }

  /**
   * Generate and append the expand button as light DOM
   * @private
   */
  #addExpandButton(): void {
    if (this.querySelector('.splitter-expand-btn')) return;
    this.insertAdjacentHTML(
      'beforeend',
      `<div class="splitter-expand-btn" role="${attributes.BUTTON}" tabindex="0" aria-label="Expand panel"></div>`
    );
    this.#attachExpandButtonEvents();
  }

  /**
   * Bound toggle handler for the expand button
   * @private
   * @param {Event} e The triggering event
   */
  #handleExpandToggle = (e: Event) => {
    if (e.type === 'keydown') {
      const keyEvent = e as KeyboardEvent;
      if (keyEvent.key !== 'Enter' && keyEvent.key !== ' ') return;
    }
    e.preventDefault();
    this.triggerEvent('expandbuttonclick', this, {
      bubbles: true,
      detail: { elem: this }
    });
  };

  /**
   * Get the expand button element
   * @returns {HTMLElement | null} The expand button element
   */
  get expandButtonEl(): HTMLElement | null {
    return this.querySelector('.splitter-expand-btn') ?? null;
  }

  /**
   * Get the drag handle element
   * @returns {HTMLElement | null} The drag handle element
   */
  get dragHandleEl(): HTMLElement | null {
    return this.querySelector('.splitter-drag-handle') ?? null;
  }

  /**
   * Set the aria-label for the split bar
   * @param {string} value The label text
   */
  set label(value: string) {
    if (value) {
      this.setAttribute(attributes.LABEL, value);
      this.setAttribute(htmlAttributes.ARIA_LABEL, value);
    } else {
      this.removeAttribute(attributes.LABEL);
      this.removeAttribute(htmlAttributes.ARIA_LABEL);
    }
  }

  get label(): string {
    return this.getAttribute(attributes.LABEL) || 'Resize';
  }

  /**
   * Set whether to show the expand button
   * @param {boolean | string} value If true, shows the expand button
   */
  set showExpandButton(value: boolean | string) {
    const val = stringToBool(value);
    this.toggleAttribute(attributes.SHOW_EXPAND_BUTTON, val);
    if (val) {
      this.#addExpandButton();
    } else {
      this.#detachExpandButtonEvents();
      this.expandButtonEl?.remove();
    }
  }

  get showExpandButton(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_EXPAND_BUTTON));
  }

  /**
   * Update the collapsed state of the expand button
   * @param {boolean} isCollapsed Whether the associated pane is collapsed
   */
  updateExpandButtonState(isCollapsed: boolean): void {
    const expandBtn = this.expandButtonEl;
    if (!expandBtn) return;

    expandBtn.classList.toggle('is-collapsed', isCollapsed);
    expandBtn.setAttribute('data-collapsed', isCollapsed ? 'true' : 'false');
    expandBtn.setAttribute(htmlAttributes.ARIA_LABEL, `${isCollapsed ? 'Expand' : 'Collapse'} panel`);
    expandBtn.setAttribute(htmlAttributes.ARIA_EXPANDED, isCollapsed ? 'false' : 'true');

    if (isCollapsed) {
      expandBtn.style.opacity = '1';
      expandBtn.style.visibility = 'visible';
    } else {
      expandBtn.style.opacity = '';
      expandBtn.style.visibility = '';
    }
  }

  /**
   * Update expand button with a predicted direction label
   * @param {string} direction 'right' for expand, 'left' for collapse
   */
  updateExpandButtonDirection(direction: string): void {
    const expandBtn = this.expandButtonEl;
    if (!expandBtn) return;

    const willExpand = direction === 'right';
    expandBtn.setAttribute(htmlAttributes.ARIA_LABEL, `${willExpand ? 'Expand' : 'Collapse'} panel`);
    expandBtn.setAttribute('data-collapsed', willExpand ? 'true' : 'false');
    expandBtn.setAttribute(htmlAttributes.ARIA_EXPANDED, willExpand ? 'false' : 'true');
  }

  /**
   * Get a transform limit value by property name
   * @param {string} key The transform limit key (e.g. 'minTransformX')
   * @returns {number} The transform limit value
   */
  getTransformLimit(key: string): number {
    switch (key) {
      case 'minTransformX': return this.minTransformX;
      case 'maxTransformX': return this.maxTransformX;
      case 'minTransformY': return this.minTransformY;
      case 'maxTransformY': return this.maxTransformY;
      default: return 0;
    }
  }

  /**
   * Set a transform limit value by property name
   * @param {string} key The transform limit key (e.g. 'minTransformX')
   * @param {number} value The transform limit value
   */
  setTransformLimit(key: string, value: number): void {
    switch (key) {
      case 'minTransformX': this.minTransformX = value; break;
      case 'maxTransformX': this.maxTransformX = value; break;
      case 'minTransformY': this.minTransformY = value; break;
      case 'maxTransformY': this.maxTransformY = value; break;
      default: break;
    }
  }

  /**
   * Adjust the position of the drag handle based on mouse position
   * @param {number} position The position value in pixels
   */
  positionDragHandle(position: number): void {
    const dragHandle = this.dragHandleEl;
    if (!dragHandle) return;

    if (this.axis === 'y') {
      dragHandle.style.left = `${position}px`;
    } else {
      dragHandle.style.top = `${position}px`;
    }
  }

  /**
   * Attach event handlers to the expand button
   * @private
   */
  #attachExpandButtonEvents(): void {
    const expandBtn = this.expandButtonEl;
    if (!expandBtn) return;

    expandBtn.addEventListener('click', this.#handleExpandToggle);
    expandBtn.addEventListener('touchstart', this.#handleExpandToggle);
    expandBtn.addEventListener('keydown', this.#handleExpandToggle);
  }

  /**
   * Detach event handlers from the expand button
   * @private
   */
  #detachExpandButtonEvents(): void {
    const expandBtn = this.expandButtonEl;
    if (!expandBtn) return;

    expandBtn.removeEventListener('click', this.#handleExpandToggle);
    expandBtn.removeEventListener('touchstart', this.#handleExpandToggle);
    expandBtn.removeEventListener('keydown', this.#handleExpandToggle);
  }
}
